import _ from 'lodash';
import PropTypes from 'prop-types';
import { globalizeSelectors } from '../Utils';
import { MACHINE_MSG_ID_OPEN_REFILL_PANEL } from '../../Constants';
import { fulfilled, rejected } from '../factories/ApiCall';
import type {
  DispenserId,
  MachineCanister,
  MachineCanisterBatch,
  MachineCanisterRefill,
  MachineControllerConfiguration,
  MachineDBRecord,
  MachineDriverConfiguration,
  MachineFormulaItem,
  MachineInfo,
  MachineState,
  MachineTintOrder,
} from 'js/api/types/Machine';
import type {
  MachineSliceAction,
  MachineApiResults,
  MachineBarcodeRefillAction,
  MachineSubSliceState,
  MachineSliceState,
  MachineWarningBatches,
  MachineBackupResponse,
  MachineSetRefillsListSagaPayload,
  MachineSetRefillsBatchListSagaPayload,
} from '../types/Machine';
import { BarcodeResolveResponse } from 'js/api/BarcodeActions';

const types = {
  FETCH_DRIVER_CONFIG: 'MACHINE/FETCH_DRIVER_CONFIG',
  SET_DRIVER_CONFIG: 'MACHINE/SET_DRIVER_CONFIG',
  CMD_SET_DRIVER_CONFIG: 'MACHINE/CMD_SET_DRIVER_CONFIG',
  FETCH_MACHINES_LIST: 'MACHINE/FETCH_MACHINES_LIST',
  SET_MACHINE_MISSING: 'MACHINE/SET_MACHINE_MISSING',
  SET_DISPIDS: 'MACHINE/SET_DISPIDS',
  MACHINE_CONFIG_VISIBLE: 'MACHINE/MACHINE_CONFIG_VISIBLE',
  SET_CURRENT_MACHINE: 'MACHINE/SET_CURRENT_MACHINE',
  SET_MACHINE_INFO: 'MACHINE/SET_MACHINE_INFO',
  SET_DBMACHINE_INFO: 'MACHINE/SET_DBMACHINE_INFO',
  FETCH_MACHINE_COLORANTS: 'MACHINE/FETCH_MACHINE_COLORANTS',
  SET_MACHINE_COLORANTS: 'MACHINE/SET_MACHINE_COLORANTS',
  CMD_SET_MACHINE_CONFIG: 'MACHINE/CMD_SET_MACHINE_CONFIG',
  CMD_SET_MACHINE_COLORANTS: 'MACHINE/CMD_SET_MACHINE_COLORANTS',
  CMD_PURGE: 'MACHINE/CMD_PURGE',
  CMD_PURGE_ALL: 'MACHINE/CMD_PURGE_ALL',
  CMD_PURGE_AUTOMATIC: 'MACHINE/CMD_PURGE_AUTOMATIC',
  CMD_PURGE_AUTOMATIC_PREPARE: 'MACHINE/CMD_PURGE_AUTOMATIC_PREPARE',
  CMD_STIR: 'MACHINE/CMD_STIR',
  CMD_STIR_ALL: 'MACHINE/CMD_STIR_ALL',
  CMD_CLEAR_DISPENSER_WARNING: 'MACHINE/CMD_CLEAR_DISPENSER_WARNING',
  CMD_CLEAR_HOPPER_WARNING: 'MACHINE/CMD_CLEAR_HOPPER_WARNING',
  CMD_MOVE_TO_FILL: 'MACHINE/CMD_MOVE_TO_FILL',
  CMD_MOVE_TO_HOME: 'MACHINE/CMD_MOVE_TO_HOME',
  FETCH_MACHINE_STATE: 'MACHINE/FETCH_MACHINE_STATE',
  FETCH_MACHINE_CONFIG: 'MACHINE/FETCH_MACHINE_CONFIG',
  FETCH_MACHINE_ERROR: 'MACHINE/FETCH_MACHINE_ERROR',
  CLEAR_MACHINE_ERROR: 'MACHINE/CLEAR_MACHINE_ERROR',
  SET_MACHINE_STATE: 'MACHINE/SET_MACHINE_STATE',
  SET_MACHINE_STATE_REDUX: 'MACHINE/SET_MACHINE_STATE_REDUX',
  FETCH_MACHINE_REFILLS_LIST: 'MACHINE/FETCH_MACHINE_REFILLS_LIST',
  SET_MACHINE_BATCH_REFILLS_LIST: 'MACHINE/SET_MACHINE_BATCH_REFILLS_LIST',
  FETCH_MACHINE_BATCH_REFILLS_LIST: 'MACHINE/FETCH_MACHINE_BATCH_REFILLS_LIST',
  SET_MACHINE_REFILLS_LIST: 'MACHINE/SET_MACHINE_REFILLS_LIST',
  SET_MACHINE_REFILLS_LIST_SAGA: 'MACHINE/SET_MACHINE_REFILLS_LIST_SAGA',
  SET_MACHINE_REFILLS_BATCH_LIST_SAGA:
    'MACHINE/SET_MACHINE_REFILLS_BATCH_LIST_SAGA',
  SET_MACHINE_CONFIG: 'MACHINE/SET_MACHINE_CONFIG',
  SET_MACHINE_ERROR: 'MACHINE/SET_MACHINE_ERROR',

  CMD_MACHINE_ACTION: 'MACHINE/CMD_MACHINE_ACTION',
  CMD_CHECK_FORMULA: 'MACHINE/CMD_CHECK_FORMULA',
  CMD_TINT: 'MACHINE/CMD_TINT',

  RESUME_INTERRUPTED_TINT: 'MACHINE/RESUME_INTERRUPTED_TINT',

  CMD_RESET: 'MACHINE/CMD_RESET',
  CMD_RESET_HARD: 'MACHINE/CMD_RESET_HARD',
  CMD_INITIALIZE: 'MACHINE/CMD_INITIALIZE',

  CMD_CAP: 'MACHINE/CMD_CAP',

  CMD_CLEAN: 'MACHINE/CMD_CLEAN',
  CMD_CLEAN_ALL: 'MACHINE/CMD_CLEAN_ALL',

  TINT_WEIGHED: 'MACHINE/TINT_WEIGHED',
  TINT_ERROR_STATUS: 'MACHINE/TINT_ERROR_STATUS',

  SET_DROP_COLORS: 'MACHINE/SET_DROP_COLORS',
  SET_MACHINE_IN_PURGE: 'MACHINE/SET_MACHINE_IN_PURGE',

  SET_MACHINE_BARCODE_REFILL_ACTION:
    'MACHINE/SET_MACHINE_BARCODE_REFILL_ACTION',
  SET_MACHINE_BARCODE_CANISTER_ACTION:
    'MACHINE/SET_MACHINE_BARCODE_CANISTER_ACTION',
  SET_MACHINE_SELECTED_CNT_CODE: 'MACHINE/SET_MACHINE_SELECTED_CNT_CODE',
  CLEAR_BARCODE_ACTIONS: 'MACHINE/CLEAR_BARCODE_ACTIONS',

  CHECK_WARNING_BATCH: 'MACHINE/CHECK_WARNING_BATCH',
  SET_WARNING_BATCH: 'MACHINE/SET_WARNING_BATCH,',

  SET_MACHINE_SLIDE_OVER_VISIBLE: 'MACHINE/SET_MACHINE_SLIDE_OVER_VISIBLE,',

  SET_REFILL_TARGET_CANISTER: 'MACHINE/SET_REFILL_TARGET_CANISTER,',

  CMD_BACKUP: 'MACHINE/CMD_BACKUP',

  FETCH_MACHINE_CONFIG_BACKUP: 'MACHINE/FETCH_MACHINE_CONFIG_BACKUP',
  DELETE_MACHINE_CONFIG_BACKUP: 'MACHINE/DELETE_MACHINE_CONFIG_BACKUP',
} as const;

const actions = {
  fetchClientData: (clientid: number) => ({
    type: types.FETCH_MACHINE_CONFIG_BACKUP,
    payload: { clientid },
  }),
  deleteClientData: (dataid: number) => ({
    type: types.DELETE_MACHINE_CONFIG_BACKUP,
    payload: { dataid },
  }),
  backup: (dispID: DispenserId, notes: string | null) => ({
    type: types.CMD_BACKUP,
    payload: { dispID, notes },
  }),
  fetchMachinesList: () => ({ type: types.FETCH_MACHINES_LIST, payload: null }),
  setMachineMissing: (value: boolean | null) => ({
    type: types.SET_MACHINE_MISSING,
    payload: value,
  }),
  setRefillTargetCanister: (
    canister: MachineSliceState['refill_target_canister']
  ) => ({
    type: types.SET_REFILL_TARGET_CANISTER,
    payload: canister,
  }),
  toggleMachineConfigVisible: (dispID: DispenserId) => ({
    type: types.MACHINE_CONFIG_VISIBLE,
    payload: { dispID },
  }),
  setMachineSlideOverVisible: (dispID: DispenserId, visible: boolean) => ({
    type: types.SET_MACHINE_SLIDE_OVER_VISIBLE,
    payload: { dispID, visible },
  }),

  setMachineBarcodeRefillAction: (
    cntid: number | null,
    canisterid: number | null
  ) => ({
    type: types.SET_MACHINE_BARCODE_REFILL_ACTION,
    payload: {
      cntid: cntid,
      cansizeid: null,
      barcode: null,
      canisterid,
    },
  }),
  checkWarningBatch: () => ({ type: types.CHECK_WARNING_BATCH }),
  setWarningBatch: (batches: MachineWarningBatches) => ({
    type: types.SET_WARNING_BATCH,
    payload: batches,
  }),
  fetchDriverConfig: () => ({
    type: types.FETCH_DRIVER_CONFIG,
    payload: null,
  }),
  setDriverConfig: (config: MachineDriverConfiguration) => ({
    type: types.CMD_SET_DRIVER_CONFIG,
    payload: { config },
  }),
  setMachineSelectedCntCode: (dispID: DispenserId, code: string | null) => ({
    type: types.SET_MACHINE_SELECTED_CNT_CODE,
    payload: { dispID, code },
  }),
  setCurrentMachine: (dispID: DispenserId) => ({
    type: types.SET_CURRENT_MACHINE,
    payload: { dispID },
  }),

  fetchMachineState: (dispID: DispenserId) => ({
    type: types.FETCH_MACHINE_STATE,
    payload: { dispID },
  }),
  setMachineState: (machinestate: string) => ({
    type: types.SET_MACHINE_STATE,
    payload: JSON.parse(machinestate),
  }),
  fetchMachineBatchRefillsList: (
    dispID: DispenserId,
    canisterIndex: number,
    currLevel: number
  ) => ({
    type: types.FETCH_MACHINE_BATCH_REFILLS_LIST,
    payload: { dispID, canisterIndex, currLevel },
  }),
  setMachineBatchRefillsList: (data: string) => ({
    type: types.SET_MACHINE_BATCH_REFILLS_LIST,
    payload: JSON.parse(data),
  }),
  fetchMachineRefillsList: (dispID: DispenserId, canisterIndex: number) => ({
    type: types.FETCH_MACHINE_REFILLS_LIST,
    payload: { dispID, canisterIndex },
  }),
  setMachineRefillsList: (refilllist: string) => ({
    type: types.SET_MACHINE_REFILLS_LIST,
    payload: JSON.parse(refilllist),
  }),
  setMachineRefillsListSaga: (data: MachineSetRefillsListSagaPayload) => ({
    type: types.SET_MACHINE_REFILLS_LIST_SAGA,
    payload: data,
  }),
  setMachineRefillsBatchListSaga: (
    data: MachineSetRefillsBatchListSagaPayload
  ) => ({
    type: types.SET_MACHINE_REFILLS_BATCH_LIST_SAGA,
    payload: data,
  }),
  fetchMachineError: (dispID: DispenserId) => ({
    type: types.FETCH_MACHINE_ERROR,
    payload: { dispID },
  }),
  clearMachineError: (dispID: DispenserId) => ({
    type: types.CLEAR_MACHINE_ERROR,
    payload: { dispID },
  }),
  clearHopperWarning: (dispID: DispenserId) => ({
    type: types.CMD_CLEAR_HOPPER_WARNING,
    payload: { dispID },
  }),
  clearDispenserWarning: (dispID: DispenserId) => ({
    type: types.CMD_CLEAR_DISPENSER_WARNING,
    payload: { dispID },
  }),

  fetchMachineConfig: (dispID: DispenserId) => ({
    type: types.FETCH_MACHINE_CONFIG,
    payload: { dispID },
  }),
  setMachineConfig: (
    dispID: DispenserId,
    config: MachineControllerConfiguration
  ) => ({
    type: types.CMD_SET_MACHINE_CONFIG,
    payload: { dispID, config: config },
  }),

  fetchMachineColorants: (dispID: DispenserId) => ({
    type: types.FETCH_MACHINE_COLORANTS,
    payload: { dispID },
  }),
  setMachineColorants: (cnts: string) => ({
    type: types.SET_MACHINE_COLORANTS,
    payload: JSON.parse(cnts),
  }),
  setMachineColorantsQT: (dispID: DispenserId, cnts: MachineCanister[]) => ({
    type: types.CMD_SET_MACHINE_COLORANTS,
    payload: { dispID, cnts: cnts },
  }),

  // Machine actions
  purge: (
    dispID: DispenserId,
    frm: MachineFormulaItem[],
    sequential: boolean | undefined = undefined
  ) => ({
    type: types.CMD_PURGE,
    payload: { dispID, frm, sequential },
  }),
  purgeAll: (
    dispID: DispenserId,
    amount: number,
    sequential: boolean | undefined = undefined
  ) => ({
    type: types.CMD_PURGE_ALL,
    payload: { dispID, amount, sequential },
  }),
  purgeAutomatic: (dispID: DispenserId) => ({
    type: types.CMD_PURGE_AUTOMATIC,
    payload: { dispID },
  }),
  purgeAutomaticPrepare: (dispID: DispenserId) => ({
    type: types.CMD_PURGE_AUTOMATIC_PREPARE,
    payload: { dispID },
  }),
  stir: (dispID: DispenserId, canid: number, time: number) => ({
    type: types.CMD_STIR,
    payload: { dispID, canid: canid, time },
  }),
  stirAll: (dispID: DispenserId, time: number) => ({
    type: types.CMD_STIR_ALL,
    payload: { dispID, time },
  }),
  clean: (dispID: DispenserId, circuitid: number) => ({
    type: types.CMD_CLEAN,
    payload: { dispID, circuitid: circuitid },
  }),
  cleanAll: (dispID: DispenserId) => ({
    type: types.CMD_CLEAN_ALL,
    payload: { dispID },
  }),
  moveToFill: (dispID: DispenserId, canid: number) => ({
    type: types.CMD_MOVE_TO_FILL,
    payload: { dispID, canid },
  }),
  moveToHome: (dispID: DispenserId) => ({
    type: types.CMD_MOVE_TO_HOME,
    payload: { dispID },
  }),
  machineAction: (dispID: DispenserId, machine_action: string) => ({
    type: types.CMD_MACHINE_ACTION,
    payload: { dispID, machine_action: machine_action },
  }),

  checkFormula: (dispID: DispenserId, frm: MachineFormulaItem[]) => ({
    type: types.CMD_CHECK_FORMULA,
    payload: { dispID, frm: frm },
  }),
  tintOrder: (
    dispID: DispenserId,
    order: MachineTintOrder,
    skip_tdf_base = false,
    barcodeValidation = false
  ) => ({
    type: types.CMD_TINT,
    payload: { dispID, order, skip_tdf_base, barcodeValidation },
  }),

  initialize: (dispID: DispenserId) => ({
    type: types.CMD_INITIALIZE,
    payload: { dispID },
  }),
  reset: (dispID: DispenserId) => ({
    type: types.CMD_RESET,
    payload: { dispID },
  }),
  resetHard: (dispID: DispenserId) => ({
    type: types.CMD_RESET_HARD,
    payload: { dispID },
  }),

  cap: (dispID: DispenserId, value: number) => ({
    type: types.CMD_CAP,
    payload: { dispID, value },
  }),

  // animation colors
  setDropColors: (colors: string[]) => ({
    type: types.SET_DROP_COLORS,
    payload: colors,
  }),

  // Clear barcode actions
  clearBarcodeActions: () => ({
    type: types.CLEAR_BARCODE_ACTIONS,
    payload: {},
  }),
};

const mountPath = 'machine'; // mount point in global state, must match root reducer

function payloadAs<Payload>(action: MachineSliceAction): Payload {
  return action.payload as Payload;
}

function responseResults<Payload>(action: MachineSliceAction): Payload {
  return payloadAs<MachineApiResults<Payload>>(action).results;
}

const propType = PropTypes.shape({
  colorants: PropTypes.arrayOf(
    //FIXME: these are actually canisters, not colorants
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      circuits: PropTypes.array.isRequired,
      barcode: PropTypes.string,
      enabled: PropTypes.bool,
      minLevel: PropTypes.number.isRequired,
      rgb: PropTypes.number,
      currLevel: PropTypes.number.isRequired,
      warnLevel: PropTypes.number.isRequired,
      maxLevel: PropTypes.number.isRequired,
    })
  ),

  config: PropTypes.shape({}),
  machine_state: PropTypes.shape({
    busy: PropTypes.bool.isRequired,
  }),
});

const initial_machine_state: MachineSubSliceState = {
  //show_machine_config: false,
  dispID: null,
  colorants: [],
  commands: {},
  config: null,
  error: null,
  info: null,
  dbinfo: null, // Info of the machine in database
  machine_state: null,
  selected_cnt_code: null,
  refills_list: [],
  slide_over_visible: false,
};
const initial_state: MachineSliceState = {
  show_config_id: null,
  current_dispID: window.qtside ? undefined : '0',
  machines: window.qtside ? {} : { 0: initial_machine_state },
  dispIDs: window.qtside ? [] : ['0'],
  machine_missing: null,
  // animation colors
  drop_colors: ['#3Ff400'],
  machines_in_purge_state: [], // machines which have started purge process
  driver_config: null,
  warning_batch: [],
  backup_in_progress: false,
  backup_response: null,
  clientdata: { data: [], pending: false, error: null },
  refill_target_canister: null, // Opens the refill modal for a specific canister
  barcode_refill_action: null, // Canister refill action read by barcode reader
  barcode_canister_action: null, // Canister barcode action read by barcode reader
};

// reducer for one machine, called by main reducer
function machineReducer(
  state: MachineSubSliceState = initial_machine_state,
  action: MachineSliceAction
): MachineSubSliceState {
  switch (action.type) {
    case types.SET_MACHINE_SLIDE_OVER_VISIBLE: {
      const payload = payloadAs<{ visible: boolean }>(action);
      return { ...state, slide_over_visible: payload.visible };
    }
    case types.SET_MACHINE_COLORANTS: {
      return {
        ...state,
        colorants: responseResults<MachineCanister[]>(action),
      };
    }
    case types.SET_MACHINE_CONFIG: {
      return {
        ...state,
        config: responseResults<MachineSubSliceState['config']>(action),
      };
    }
    case types.SET_MACHINE_ERROR: {
      return {
        ...state,
        error: responseResults<MachineSubSliceState['error']>(action),
      };
    }
    case types.SET_MACHINE_INFO: {
      const { info, commands } = responseResults<{
        info: MachineInfo | null;
        commands: MachineSubSliceState['commands'];
      }>(action);
      return { ...state, info, commands };
    }
    case types.SET_DBMACHINE_INFO: {
      return {
        ...state,
        dbinfo: responseResults<MachineDBRecord | null>(action),
      };
    }
    case types.SET_MACHINE_STATE_REDUX: {
      const results = responseResults<MachineState | null>(action);
      return {
        ...state,
        machine_state: results,
        slide_over_visible: results?.msgID === MACHINE_MSG_ID_OPEN_REFILL_PANEL,
      };
    }
    case types.SET_MACHINE_REFILLS_LIST: {
      return {
        ...state,
        refills_list: responseResults<MachineCanisterRefill[]>(action),
      };
    }
    case types.SET_MACHINE_BATCH_REFILLS_LIST: {
      return {
        ...state,
        refills_batch_list: responseResults<MachineCanisterBatch[]>(action),
      };
    }
    case types.SET_MACHINE_SELECTED_CNT_CODE: {
      const payload = payloadAs<{ code: string | null }>(action);
      return { ...state, selected_cnt_code: payload.code };
    }

    default: {
      return state;
    }
  }
}

function reducer(
  state: MachineSliceState = initial_state,
  action: MachineSliceAction
): MachineSliceState {
  switch (action.type) {
    // actions not related to a machine
    case types.SET_MACHINE_IN_PURGE: {
      const { dispID, purging } = payloadAs<{
        dispID: DispenserId;
        purging: boolean;
      }>(action);
      if (purging) {
        let tmp = _.union(state.machines_in_purge_state, [dispID]);
        return {
          ...state,
          machines_in_purge_state: tmp,
        };
      } else {
        return {
          ...state,
          machines_in_purge_state: _.difference(state.machines_in_purge_state, [
            dispID,
          ]),
        };
      }
    }
    case types.SET_WARNING_BATCH: {
      return {
        ...state,
        warning_batch: payloadAs<MachineWarningBatches>(action),
      };
    }
    case types.CMD_BACKUP: {
      return { ...state, backup_in_progress: true, backup_response: null };
    }
    case fulfilled(types.CMD_BACKUP): {
      return {
        ...state,
        backup_in_progress: false,
        backup_response: payloadAs<MachineBackupResponse>(action),
      };
    }
    case rejected(types.CMD_BACKUP): {
      return {
        ...state,
        backup_in_progress: false,
        backup_response: payloadAs<MachineBackupResponse>(action),
      };
    }
    case types.FETCH_MACHINE_CONFIG_BACKUP: {
      return { ...state, clientdata: { data: [], pending: true, error: null } };
    }
    case fulfilled(types.FETCH_MACHINE_CONFIG_BACKUP): {
      return {
        ...state,
        clientdata: {
          data: payloadAs<MachineSliceState['clientdata']['data']>(action),
          pending: false,
          error: null,
        },
      };
    }
    case rejected(types.FETCH_MACHINE_CONFIG_BACKUP): {
      return {
        ...state,
        clientdata: { data: [], pending: false, error: action.payload },
      };
    }
    case types.SET_DROP_COLORS: {
      return { ...state, drop_colors: payloadAs<string[]>(action) };
    }
    case types.SET_DISPIDS: {
      return { ...state, dispIDs: payloadAs<DispenserId[]>(action) };
    }
    case types.SET_MACHINE_MISSING: {
      return {
        ...state,
        machine_missing:
          payloadAs<MachineSliceState['machine_missing']>(action),
      };
    }
    case types.MACHINE_CONFIG_VISIBLE: {
      const { dispID } = payloadAs<{ dispID: DispenserId }>(action);
      const show_config_id = dispID !== state.show_config_id ? dispID : null;
      return { ...state, show_config_id };
    }
    case types.SET_CURRENT_MACHINE: {
      const current_dispID = payloadAs<{ dispID: DispenserId }>(action).dispID;
      return { ...state, current_dispID };
    }
    case types.SET_DRIVER_CONFIG: {
      return {
        ...state,
        driver_config: payloadAs<MachineSliceState['driver_config']>(action),
      };
    }

    case types.SET_REFILL_TARGET_CANISTER: {
      return {
        ...state,
        refill_target_canister:
          payloadAs<MachineSliceState['refill_target_canister']>(action),
      };
    }
    case types.SET_MACHINE_BARCODE_REFILL_ACTION: {
      return {
        ...state,
        barcode_refill_action: payloadAs<MachineBarcodeRefillAction>(action),
      };
    }
    case types.SET_MACHINE_BARCODE_CANISTER_ACTION: {
      return {
        ...state,
        barcode_canister_action: payloadAs<BarcodeResolveResponse>(action),
      };
    }
    case types.CLEAR_BARCODE_ACTIONS: {
      return {
        ...state,
        barcode_canister_action: null,
        barcode_refill_action: null,
      };
    }

    // actions for a specific machine
    default: {
      const dispID = _.get(action, 'payload.dispID') as DispenserId | undefined;
      if (dispID != null) {
        const mstate = state.machines[dispID];
        let new_mstate = machineReducer(mstate, action);
        if (!mstate) {
          new_mstate = { ...new_mstate, dispID };
        }
        if (new_mstate !== mstate) {
          return {
            ...state,
            machines: { ...state.machines, [dispID]: new_mstate },
          };
        }
      }
      return state;
    }
  }
}

const localSelectors = {
  driver_config: (
    state: MachineSliceState
  ): MachineSliceState['driver_config'] => state.driver_config,
  refills_list: (
    state: MachineSliceState,
    dispID: DispenserId
  ): MachineSubSliceState['refills_list'] | undefined =>
    state.machines[dispID]?.refills_list,
  refills_batch_list: (
    state: MachineSliceState,
    dispID: DispenserId
  ): MachineSubSliceState['refills_batch_list'] =>
    state.machines[dispID]?.refills_batch_list,
  drop_colors: (state: MachineSliceState): string[] => state.drop_colors,
  machine: (
    state: MachineSliceState,
    dispID: DispenserId | undefined
  ): MachineSubSliceState | undefined =>
    dispID == null ? undefined : state.machines[dispID],
  isMachineMissing: (
    state: MachineSliceState
  ): MachineSliceState['machine_missing'] => state.machine_missing,
  machines: (state: MachineSliceState): MachineSliceState['machines'] =>
    state.machines,
  dispIDs: (state: MachineSliceState): DispenserId[] => state.dispIDs,
  show_config_id: (
    state: MachineSliceState
  ): MachineSliceState['show_config_id'] => state.show_config_id,
  current_dispID: (
    state: MachineSliceState
  ): MachineSliceState['current_dispID'] => state.current_dispID,
  current_machine: (
    state: MachineSliceState
  ): MachineSubSliceState | undefined =>
    state.current_dispID != null
      ? state.machines[state.current_dispID]
      : undefined,
  is_purging: (state: MachineSliceState, dispID: DispenserId): boolean =>
    _.includes(state.machines_in_purge_state, dispID),
  machinesColorants: (state: MachineSliceState): MachineCanister[] =>
    _.map(state.machines, (machine) => machine?.colorants ?? []).flat(),
  warning_batch: (state: MachineSliceState): MachineWarningBatches =>
    state.warning_batch,
  refill_target_canister: (
    state: MachineSliceState
  ): MachineSliceState['refill_target_canister'] =>
    state.refill_target_canister,
  machinesStates: (state: MachineSliceState): Array<MachineState | null> =>
    _.map(state.machines, (machine) => machine?.machine_state ?? null),
  slideOverVisible: (
    state: MachineSliceState,
    dispID: DispenserId
  ): boolean | undefined => state.machines[dispID]?.slide_over_visible,
  find_dispID_canister: (
    state: MachineSliceState,
    barcode: string
  ): [DispenserId, MachineCanister] | [] => {
    for (let dispID of state.dispIDs) {
      const canister = state.machines[dispID]!.colorants?.find(
        (c: MachineCanister) => c.barcode === barcode
      );
      if (canister) {
        return [dispID, canister];
      }
    }
    return [];
  },
  find_dispID_canisters: (
    state: MachineSliceState,
    cntid: number
  ): [DispenserId | null, MachineCanister[]] => {
    for (let dispID of state.dispIDs) {
      const canisters = state.machines[dispID]!.colorants?.filter(
        (canister: MachineCanister) => canister.cntid === cntid
      );
      if (canisters.length) {
        return [dispID, canisters];
      }
    }
    return [null, []];
  },
  backup_in_progress: (state: MachineSliceState): boolean =>
    state.backup_in_progress,
  clientdata: (state: MachineSliceState): MachineSliceState['clientdata'] =>
    state.clientdata,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  propType,
  selectors,
  reducer,
};
